import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { homePath } from "../lib/api";
import { BookOpen, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      navigate(homePath(user), { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      const dest = homePath(res.user);
      navigate(location.state?.from?.pathname || dest, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <div className="hidden lg:flex w-[46%] bg-primary text-primary-foreground flex-col justify-between p-14 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3 font-display text-3xl font-bold">
            <span className="w-10 h-10 rounded-md bg-accent text-accent-foreground flex items-center justify-center"><BookOpen size={20} /></span> Khata
          </div>
          <p className="text-primary-foreground/60 mt-3 text-xs uppercase tracking-[0.18em]">
            Cash Book &amp; Credit Ledger
          </p>
        </div>
        <div className="relative z-10 space-y-6">
          <h2 className="font-display text-5xl leading-[1.05] font-bold">
            Every rupee,
            <br /> <span className="text-accent">accounted for.</span>
          </h2>
          <p className="text-primary-foreground/70 max-w-sm text-sm leading-relaxed">
            Opening balances, serial invoices, bank vs cash receipts, and party credit limits —
            all balanced in one ledger.
          </p>
          <div className="rounded-md border border-white/10 bg-white/5 max-w-sm overflow-hidden text-xs">
            {[["Opening balance", "1,25,000.00"], ["Cash received", "48,350.00"], ["Paid out", "− 12,800.00"]].map(([l, v]) => (
              <div key={l} className="flex justify-between px-4 py-2.5 border-b border-white/10"><span className="text-primary-foreground/70">{l}</span><span className="num">₹ {v}</span></div>
            ))}
            <div className="flex justify-between px-4 py-2.5 font-semibold"><span>Closing cash</span><span className="num text-accent">₹ 1,60,550.00</span></div>
          </div>
        </div>
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, white 0, white 1px, transparent 1px, transparent 42px), linear-gradient(90deg, transparent 63px, white 63px, white 64px, transparent 64px)",
          }}
        />
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up bg-card border border-border rounded-lg shadow-sm p-7 sm:p-8" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 font-display text-2xl font-bold mb-6">
            <span className="w-8 h-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center"><BookOpen size={16} /></span> Khata
          </div>
          <h1 className="font-display text-2xl font-bold">Sign in</h1>
          <p className="text-muted-foreground text-sm mt-1 mb-7">
            Enter your credentials to open the ledger.
          </p>

          <label className="block text-sm font-medium mb-1.5">Email</label>
          <input
            data-testid="login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-11 px-3 rounded-md border border-input bg-card text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-ring/40"
            placeholder="you@company.com"
          />
          <label className="block text-sm font-medium mb-1.5">Password</label>
          <input
            data-testid="login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full h-11 px-3 rounded-md border border-input bg-card text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-ring/40"
            placeholder="••••••••"
          />
          <div className="flex justify-end mb-2">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              Forgot password?
            </Link>
          </div>
          {error && (
            <p data-testid="login-error" className="text-destructive text-sm mb-2">
              {error}
            </p>
          )}
          <button
            data-testid="login-submit"
            disabled={loading}
            className="mt-4 w-full h-11 rounded-md bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 active:scale-[0.99] transition-[background-color,transform] disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"} <ArrowRight size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}
